import { UseSelectionResult } from '../hooks';
import { Button, DropdownMenu, Text } from '@radix-ui/themes';
import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { SelectionDropdownItem } from './SelectionDropdownItem';

interface SelectionDropdownProps<TData extends { id: string }> {
    selectionState: UseSelectionResult<TData>;
    displayKey: keyof TData;
    onToggleRowSelection: (rowId: string) => void;
}

const getRowDisplayLabel = <TData extends { id: string }>(
    rowId: string,
    selectedItems: UseSelectionResult<TData>['selectedItems'],
    displayKey: keyof TData,
): string => {
    const rowData = selectedItems[rowId];
    if (rowData && rowData[displayKey]) return String(rowData[displayKey]);
    return rowId;
};

export const SelectionDropdown = <TData extends { id: string }>({
    selectionState,
    displayKey,
    onToggleRowSelection,
}: SelectionDropdownProps<TData>) => {
    const { t } = useTranslation('table');
    const { selectedItems } = selectionState;
    const allSelectedRowIds = useMemo(
        () => Object.keys(selectedItems),
        [selectedItems],
    );
    const selectionCount = allSelectedRowIds.length;

    const selectedRowsDropdownItems = useMemo(() => {
        return allSelectedRowIds.map((rowId) => {
            const label = getRowDisplayLabel(rowId, selectedItems, displayKey);

            return (
                <SelectionDropdownItem
                    key={rowId}
                    rowId={rowId}
                    label={label}
                    onToggleRowSelection={onToggleRowSelection}
                />
            );
        });
    }, [allSelectedRowIds, selectedItems, displayKey, onToggleRowSelection]);

    if (!selectionCount) return null;

    return (
        <DropdownMenu.Root>
            <DropdownMenu.Trigger disabled={!selectionCount}>
                <Button variant='soft'>
                    <Text>
                        <Trans
                            i18nKey={'table:selection'}
                            count={selectionCount}
                        />
                    </Text>
                    <DropdownMenu.TriggerIcon />
                </Button>
            </DropdownMenu.Trigger>

            <DropdownMenu.Content style={{ maxHeight: '200px' }}>
                {selectionCount > 0 ? (
                    selectedRowsDropdownItems
                ) : (
                    <DropdownMenu.Item disabled>
                        <Text>{t('noSelection')}</Text>
                    </DropdownMenu.Item>
                )}
            </DropdownMenu.Content>
        </DropdownMenu.Root>
    );
};
